import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { User, UserUpdateBody } from "../api/models/user.models";
import useSelf from "../hooks/useSelf";
import { useUIStore } from "../stores/ui-store";
import { useShallow } from "zustand/shallow";
import { Locale, UIState } from "../types/ui-types";
import { useTranslation } from "react-i18next";

type UserContextProps = {
  user?: User;
  isLoadingUser: boolean;
  updateUserLanguage: any;
};

export const UserContext = createContext<UserContextProps>({
  user: undefined,
  isLoadingUser: false,
  updateUserLanguage: null,
});

type UserProviderProps = {
  children: ReactNode;
};

export const UserProvider = ({ children }: UserProviderProps) => {
  const { t } = useTranslation();
  const [toast, setLocale, locale] = useUIStore(
    useShallow((state: UIState) => [
      state.toastRef,
      state.setLocale,
      state.locale,
    ])
  );

  const getLocaleFromUserData = (language: "FI" | "EN" | "SV" | null) => {
    switch (language) {
      case "FI":
        return Locale.FI;
      case "EN":
        return Locale.EN;
      case "SV":
        return Locale.SWE;
      default:
        return Locale.FI;
    }
  };

  const { userData, isLoadingUser, updateUserInfo, isUpdatingUser } = useSelf();

  const updateUserLanguage = async (language: "FI" | "EN" | "SV") => {
    if (userData) {
      const payload: UserUpdateBody = {
        ...userData,
        language: language,
      };

      updateUserInfo(payload);
    } else {
      toast?.current?.show({
        severity: "warn",
        detail: t("missing-user-info"),
        life: 3000,
      });
    }
  };

  useEffect(() => {
    // If userdata language is null, update FI as default language
    if (userData && userData.language === null) {
      const payload: UserUpdateBody = {
        ...userData,
        language: "FI",
      };

      updateUserInfo(payload);
    } else if (userData) {
      const storedLocale = getLocaleFromUserData(userData.language);
      locale !== storedLocale && setLocale(storedLocale);
    }
  }, [userData]);

  const value: UserContextProps = {
    user: userData,
    isLoadingUser: isLoadingUser,
    updateUserLanguage,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => useContext(UserContext);
